<template>
  <!-- Bottom color bar + detection overlay -->
  <div v-if="agentStore.wakeWordActive" class="fixed bottom-0 left-0 right-0 z-30 pb-safe pointer-events-none">
    <!-- Detection flash overlay -->
    <transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-500"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0">
      <div
        v-if="showOverlay"
        class="fixed inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm pointer-events-auto cursor-pointer"
        @click="agentStore.cancelWakeWordCapture()">
        <div
          class="flex flex-col items-center gap-3 bg-white dark:bg-gray-800 rounded-2xl px-8 py-6 shadow-2xl border border-gray-200 dark:border-gray-700"
          @click.stop>
          <img
            :src="brand.images.logoHeader"
            :alt="brand.agentName"
            class="w-14 h-14 rounded-full"
            :class="{ 'animate-pulse': agentStore.wakeWordProcessing }" />
          <p class="text-sm font-semibold text-gray-900 dark:text-white">
            {{ overlayText }}
          </p>
          <!-- Mini level bar in overlay -->
          <div
            v-if="agentStore.wakeWordListening"
            class="w-48 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              class="h-full bg-purple-500 rounded-full transition-all duration-75"
              :style="{ width: `${Math.max(4, agentStore.wakeWordAudioLevel * 100)}%` }" />
          </div>
          <div v-if="agentStore.wakeWordProcessing" class="flex gap-1 mt-1">
            <div class="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
            <div class="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
            <div class="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
          </div>
          <button
            @click="agentStore.cancelWakeWordCapture()"
            class="mt-1 px-4 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors">
            {{ tt('Cancel', 'Prekliči') }}
          </button>
        </div>
      </div>
    </transition>

    <!-- Mic problem hint (no audio reaching the app) -->
    <div
      v-if="agentStore.wakeWordMicWarning"
      class="mx-auto mb-1 w-fit max-w-[92%] px-3 py-1 rounded-full text-[11px] font-medium text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-900/40">
      {{ wakeWordWarning }}
    </div>

    <!-- Thin audio level bar -->
    <div class="h-1 w-full transition-colors duration-300" :class="barColorClass">
      <div
        class="h-full rounded-r-full transition-all duration-75"
        :class="barFillClass"
        :style="{ width: `${Math.max(2, agentStore.wakeWordAudioLevel * 100)}%` }" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useAgentStore } from '../stores/agent';
import { brand, heyAgent } from '../config/brand';
import { tt } from '../utils/consoleText';

const agentStore = useAgentStore();

const showOverlay = computed(
  () => agentStore.wakeWordDetected || agentStore.wakeWordListening || agentStore.wakeWordProcessing,
);

const overlayText = computed(() => {
  if (agentStore.wakeWordProcessing) return tt('Processing...', 'Obdelujem...');
  if (agentStore.wakeWordListening) return tt('Listening...', 'Poslušam...');
  if (agentStore.wakeWordDetected) return `${heyAgent}!`;
  return '';
});

/**
 * The mic warnings this bar shows are worded where the audio is: the store's
 * `permission-denied` branch and `WakeWordService.notifyNoAudio` hand over an
 * English sentence (and log it) and this bar is their only reader, so the pair
 * lives here rather than in the service.
 *
 * A sentence this table does not know — a new reason from the service, a
 * message from somewhere else — paints exactly as it arrived: the fallback is
 * the sentence itself, never a blank and never a guess.
 */
const MIC_WARNING_PAIRS: Array<[en: string, sl: string]> = [
  [
    'Microphone needs permission — tap “Wake word” to start it.',
    'Mikrofon potrebuje dovoljenje — pritisnite »Aktivacijska beseda«, da se zažene.',
  ],
  [
    'No mic signal is reaching the app (browser audio is not flowing). Try a different mic or restart the browser.',
    'V aplikacijo ne prihaja signal z mikrofona (zvok brskalnika se ne pretaka). Poskusite z drugim mikrofonom ali znova zaženite brskalnik.',
  ],
  [
    'Mic is on but silent — check the Windows input level or switch mic (Bluetooth headsets stay silent outside calls).',
    'Mikrofon je vklopljen, a tih — preverite vhodno raven v sistemu Windows ali zamenjajte mikrofon (Bluetooth slušalke so zunaj klicev tihe).',
  ],
  ['MediaRecorder capture could not start.', 'Zajemanja z MediaRecorder ni bilo mogoče zagnati.'],
  ['MediaRecorder capture failed.', 'Zajemanje z MediaRecorder ni uspelo.'],
];

const wakeWordWarning = computed(() => {
  const reason = agentStore.wakeWordMicWarning;
  if (!reason) return '';
  const pair = MIC_WARNING_PAIRS.find(([en]) => en === reason);
  return tt(reason, pair ? pair[1] : reason);
});

const barColorClass = computed(() => {
  if (agentStore.wakeWordProcessing) return 'bg-yellow-200 dark:bg-yellow-900/40';
  if (agentStore.wakeWordListening || agentStore.wakeWordDetected) return 'bg-purple-200 dark:bg-purple-900/40';
  return 'bg-gray-200/60 dark:bg-gray-700/40';
});

const barFillClass = computed(() => {
  if (agentStore.wakeWordProcessing) return 'bg-yellow-500';
  if (agentStore.wakeWordListening || agentStore.wakeWordDetected) return 'bg-purple-500';
  return 'bg-gray-400 dark:bg-gray-500';
});
</script>
